import type React from 'react';
import { Suspense } from 'react';

import { EntityPageContractType } from '@xing-com/crate-common-graphql-types';
import { EmployerBrandingFlag } from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useFeatureSwitch } from '@xing-com/hub';
import { SkeletonImage } from '@xing-com/skeleton';
import { useMediaListener } from '@xing-com/util';

import { EditCoverMenu } from '../edit/edit-cover/edit-cover-menu';

import { CoverMedia } from './cover-media';
import * as Styled from './cover-media.styles';
import FreeCover from './free-cover.svg';
import { PreloadCover } from './preload-cover';
import { CoverImages, useCoverMedia } from './use-cover-media';

export const ENTITY_PAGES_UNFENCE_HEADER = 'entity_pages_unfence_header';

export interface CoverMediaImagesUrlType {
  [reference: string]: string;
}

const getImagesUrl = (
  coverData: CoverImages
): CoverMediaImagesUrlType | undefined => {
  if (!coverData) {
    return undefined;
  }

  return coverData.reduce<Record<string, string>>(
    (obj, item) => Object.assign(obj, { [String(item.reference!)]: item.url }),
    {}
  );
};

type CoverMediaContainerProps = {
  canBeEdited?: boolean;
};
const CoverMediaContainerInner: React.FC<CoverMediaContainerProps> = () => {
  const { isEditing } = useEditContext();
  const {
    pageContext: { contractType, isCompanyPage, isInsiderPage },
  } = usePageContext();
  const breakpoint = useMediaListener();
  const hasFenceFS = useFeatureSwitch(ENTITY_PAGES_UNFENCE_HEADER, false);

  const isFreePage = contractType === EntityPageContractType.Free;
  const isFreeHeaderProfile = hasFenceFS && isFreePage && isCompanyPage;

  const { coverImages, updateCoverCache } = useCoverMedia();

  if ((!hasFenceFS && isFreePage) || (isFreeHeaderProfile && !isEditing)) {
    return <CoverMedia noImage={true} src={FreeCover} />;
  }

  const covers = getImagesUrl(coverImages);
  const cover = covers?.[breakpoint];

  return (
    <>
      {coverImages ? <PreloadCover covers={coverImages} /> : null}

      <CoverMedia src={cover ?? FreeCover} noImage={!cover} />

      {isEditing && !isInsiderPage ? (
        <Styled.EditingWrapperCover>
          <>
            {isFreeHeaderProfile ? (
              <EmployerBrandingFlag hasImage={!!coverImages || !!cover} />
            ) : null}
            <EditCoverMenu
              hasCover={!!coverImages || !!cover}
              onDeleteCover={() => updateCoverCache()}
              onUpdateCover={(coverUrl) => updateCoverCache(coverUrl)}
            />
          </>
        </Styled.EditingWrapperCover>
      ) : null}
    </>
  );
};

export const CoverMediaContainer: React.FC<CoverMediaContainerProps> = (
  props
) => {
  return (
    <Suspense
      fallback={
        <Styled.CoverImageWrapper>
          <Styled.CoverImageInner as={SkeletonImage} />
        </Styled.CoverImageWrapper>
      }
    >
      <CoverMediaContainerInner {...props} />
    </Suspense>
  );
};
